/**
 * Own frame (friendly side of the top `FrameCluster`, #421). Own hp bar. Same
 * data-* contract as TargetFrame; WP8 e2e uses these.
 *
 * Layout (#421): this component no longer self-positions (`position:
 * absolute` + a hardcoded `left: %`) — it renders as an ordinary flex child
 * of `FrameCluster`'s friendly (left) group, alongside `FriendlyTargetFrame`,
 * extending the same friendly-left/enemy-right convention `UnitFrameStack`
 * (#138) already used for the bottom roster.
 *
 * Champion color (#140): the frame border + "you" label are tinted with the
 * viewer's CHAMPION color (identity accent), while the HP bar stays
 * health-fraction colored — so both signals coexist (identity accent + health).
 * The own champion is the LOCAL PLAYER'S actual picked champion (#244
 * `match.champion` action, `state.ownChampionId`), falling back to
 * `DEFAULT_HUD_CHAMPION_ID` only pre-pick/pre-match when it's still null —
 * the SAME resolution convention `ActionBar.tsx` uses (#271/#275).
 */

import type { ReactElement } from "react";
import { useStore } from "../store/context.js";
import { ownEntity } from "./hud-util.js";
import { championColorCss } from "../champions/championColors.js";
import { DEFAULT_HUD_CHAMPION_ID } from "../champions/abilityLabels.js";
import { UnitFrame } from "./UnitFrame.js";

export function OwnFrame(): ReactElement {
  const { state } = useStore();
  const me = ownEntity(state.hudSnapshot, state.ownSeat);
  // The LOCAL PLAYER'S actual picked champion, not a hardcoded placeholder —
  // falls back to DEFAULT_HUD_CHAMPION_ID only pre-pick/pre-match (matches
  // ActionBar's convention, #271/#275).
  const championId = state.ownChampionId ?? DEFAULT_HUD_CHAMPION_ID;
  const accent = championColorCss(championId);

  return (
    <UnitFrame
      entity={me}
      testId="own-frame"
      align="left"
      championId={championId}
      accentColor={accent}
      dimWhenEmpty={false}
      emptyText="—"
      extraAttributes={{
        "data-champion-color": accent,
      }}
      tick={state.hudSnapshot?.tick ?? 0}
      header={<div style={{ fontSize: 12, marginBottom: 4, color: accent }}>you</div>}
    />
  );
}
